/**
 * Admin V3 — Drivers — Suspension Panel (Plan §2.2 M-F-8).
 * Wraps POST /admin/drivers/{id}/suspend / reactivate / archive / force_eligible.
 */
import React, { useState } from 'react';
import { Pause, Play, Archive, Shield } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { t } from '../../i18n';

// RBAC HIDE gating (P0-1a) — permission strings mirror the backend
// @rbac_permission on POST /admin/drivers/{id}/{suspend,reactivate,archive,
// force_eligible} (admin_driver_compliance.py). Fail-closed HIDE: can() is
// false when the permission set is unresolved. Backend remains authoritative.
const PERM_SUSPEND = 'admin:driver:suspend';
const PERM_REACTIVATE = 'admin:driver:reactivate';
const PERM_ARCHIVE = 'admin:driver:archive';
const PERM_FORCE_ELIGIBLE = 'admin:driver:force_eligible';

const DriverSuspensionPanel = ({ driver, locale = 'fr', onChange }) => {
  const driverId = driver && driver.id;
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState(null);
  const { can } = usePermissions();

  const suspend     = useAdminMutation(driverId ? `/admin/drivers/${driverId}/suspend` : '/noop', 'post');
  const reactivate  = useAdminMutation(driverId ? `/admin/drivers/${driverId}/reactivate` : '/noop', 'post');
  const archive     = useAdminMutation(driverId ? `/admin/drivers/${driverId}/archive` : '/noop', 'post');
  const forceEligible = useAdminMutation(driverId ? `/admin/drivers/${driverId}/force_eligible` : '/noop', 'post');

  const runMutation = async (mut, requiresReason) => {
    setFeedback(null);
    if (requiresReason && !reason.trim()) {
      setFeedback({ kind: 'error', msg: t('compliance.rejectModal.error.reason', locale) });
      return;
    }
    const fd = new FormData();
    if (requiresReason) fd.append('reason', reason);
    await mut.mutate(requiresReason ? fd : {}, {
      onSuccess: () => {
        setReason('');
        if (onChange) onChange();
      },
      onError: () => setFeedback({ kind: 'error', msg: t('compliance.common.error', locale) }),
    });
  };

  const isSuspended = (driver && driver.lifecycle_state) === 'suspended';

  return (
    <SectionCard
      icon={Pause}
      iconClass="bg-rose-100 text-rose-700"
      title={t('compliance.suspension.title', locale)}
    >
      <div className="space-y-4" data-testid="driver-suspension-panel">
        {(can(PERM_SUSPEND) || can(PERM_FORCE_ELIGIBLE)) && (
          <div>
            <label className="block text-sm text-slate-700 mb-1">
              {t('compliance.suspension.field.reason', locale)}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              rows={2}
              data-testid="driver-suspension-reason"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!isSuspended && can(PERM_SUSPEND) && (
            <Button
              type="button"
              onClick={() => runMutation(suspend, true)}
              disabled={suspend.loading}
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700"
              data-testid="driver-suspend-btn"
            >
              <Pause className="h-4 w-4" />
              {t('compliance.suspension.action.suspend', locale)}
            </Button>
          )}
          {isSuspended && can(PERM_REACTIVATE) && (
            <Button
              type="button"
              onClick={() => runMutation(reactivate, false)}
              disabled={reactivate.loading}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="driver-reactivate-btn"
            >
              <Play className="h-4 w-4" />
              {t('compliance.suspension.action.reactivate', locale)}
            </Button>
          )}
          {can(PERM_ARCHIVE) && (
            <Button
              type="button"
              onClick={() => runMutation(archive, false)}
              disabled={archive.loading}
              variant="outline"
              className="inline-flex items-center gap-2"
              data-testid="driver-archive-btn"
            >
              <Archive className="h-4 w-4" />
              {t('compliance.suspension.action.archive', locale)}
            </Button>
          )}
          {can(PERM_FORCE_ELIGIBLE) && (
            <Button
              type="button"
              onClick={() => runMutation(forceEligible, true)}
              disabled={forceEligible.loading}
              variant="outline"
              className="inline-flex items-center gap-2"
              data-testid="driver-force-eligible-btn"
            >
              <Shield className="h-4 w-4" />
              {t('compliance.suspension.action.force_eligible', locale)}
            </Button>
          )}
        </div>

        {feedback && (
          <div className={`text-sm ${feedback.kind === 'success' ? 'text-emerald-700' : 'text-rose-700'}`}>
            {feedback.msg}
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default DriverSuspensionPanel;
